import { format } from 'date-fns/format';
import config from '../../../../../config';
import { removeComas, removePhoneCountryCode, removeWhiteSpaces } from '../../../../../utils/format';

export interface CompletedVerification {
  /**
   * Opaque handle to the identity the api verified and now holds. Replaces the provider access
   * token we used to keep in the basket: BankId only signs those for 5 minutes, which is far
   * less than a customer needs to get through the funnel.
   */
  handle: string;
  formValues: Partial<FormValues>;
  expiresAt: string;
}

interface VerifiedUserData {
  address?: {
    streetWithNumber: string;
    postalCode: string;
    city: string;
  };
  familiyName?: string;
  givenName?: string;
  name?: string;
  phoneNumber?: string;
  birthdate?: string;
  email?: string;
}
export interface FormValues {
  email: string;
  mobilePhone: string;
  firstName: string;
  lastName: string;
  street: string;
  zip: string;
  birthDate: string;
  houseNumber: string;
  houseNoAlpha: string;
  city: string;
}

export const toFormValues = (verificationServiceResponse: VerifiedUserData): Partial<FormValues> => {
  const resp = verificationServiceResponse;
  return {
    street: removeComas(resp.address?.streetWithNumber || ''),
    zip: resp.address?.postalCode || '',
    city: resp.address?.city || '',
    lastName: resp.familiyName?.trim() || '',
    firstName: resp.givenName?.trim() || '', //or resp.name
    email: resp.email?.trim() || '',
    mobilePhone: removeWhiteSpaces(removePhoneCountryCode(resp.phoneNumber || '')),
    birthDate: resp.birthdate ? format(new Date(resp.birthdate), 'ddMMyy') : '',
  };
};

export const startCustomerVerification = async (providerName: string): Promise<string> => {
  return fetch(`${config.apiUrl}/customerverification/init?provider=${providerName}&callbackUrl=${location.origin}/callback`).then(
    (response) => response.text(),
  );
};

/**
 * Exchanges the provider's authorization code for verified user data in a single call. The api
 * keeps the data and the access token; we only ever see the handle.
 */
export const completeVerification = async (
  code: string,
  providerName: string,
  useIsoBirthdate = false,
): Promise<CompletedVerification> => {
  const response = await fetch(`${config.apiUrl}/customerverification/complete`, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json;charset=UTF-8',
    },
    body: JSON.stringify({ code, provider: providerName, callbackUrl: `${location.origin}/callback` }),
  });
  if (!response.ok) {
    throw new Error('Could not complete customer verification');
  }
  const { handle, userData, expiresAt }: { handle: string; userData?: VerifiedUserData; expiresAt: string } =
    await response.json();
  const formValues = userData ? toFormValues(userData) : {};
  if (userData && useIsoBirthdate) {
    formValues.birthDate = userData.birthdate;
  }
  return { handle, formValues, expiresAt };
};

export const registerContactKSButtonClick = (): Promise<string> => {
  return fetch(`${config.apiUrl}/customerverification/registerContactKSButtonClick`).then((response) => response.text());
};
